/**
 * Custom fetch mutator for Orval-generated client
 * Automatically adds Content-Type: application/json for requests with body
 * Handles FormData/Blob uploads correctly
 * Always includes credentials for cookie-based auth
 */

import { IMPERSONATION_USER_ID_KEY, IMPERSONATION_ACCOUNT_ID_KEY, IMPERSONATION_HEADER, clearImpersonation } from '@/lib/impersonation'
import { translateErrorCode } from '@/lib/ord-error-messages'

function resolveUrl(url: string): string {
    // В Node (server components, route handlers) относительный URL в fetch упадёт.
    // Префиксуем абсолютным базовым адресом. В браузере — оставляем как есть,
    // чтобы Next-ISR/edge-кэш работали по относительному пути.
    if (typeof window !== 'undefined') return url
    if (/^https?:\/\//i.test(url)) return url
    const base =
        process.env.DOTNET_API_BASE ||
        process.env.NEXT_PUBLIC_DOTNET_API_BASE
    if (!base) {
        throw new Error(
            'customFetch: server-side call requires DOTNET_API_BASE or NEXT_PUBLIC_DOTNET_API_BASE env var'
        )
    }
    return `${base.replace(/\/$/, '')}${url.startsWith('/') ? url : `/${url}`}`
}

export const customFetch = async <T>(
    url: string,
    options?: RequestInit
): Promise<T> => {
    const hasBody = options?.body !== undefined
    const headers = new Headers(options?.headers || {})

    // Only add Content-Type for JSON payloads (not FormData/Blob)
    // Browser automatically sets correct Content-Type for FormData with boundary
    const isFormDataOrBlob = options?.body instanceof FormData || options?.body instanceof Blob

    if (hasBody && !isFormDataOrBlob && !headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json')
    }

    // Inject impersonation header if admin is viewing as another user.
    // Также перебиваем X-Account-Id на закреплённый кабинет impersonated юзера —
    // иначе BFF подставит admin'ский metka.accountId cookie, и backend
    // зарезолвит контекст не для того кабинета.
    //
    // Исключение: admin-endpoint'ы (/api/v1/admin/*) — это инструменты самого админа,
    // на них имперсонация не нужна и ломает мутации (middleware блокирует не-GET
    // запросы в режиме имперсонации → 403 "Impersonation is read-only").
    if (typeof window !== 'undefined') {
        const impersonateUserId = sessionStorage.getItem(IMPERSONATION_USER_ID_KEY)
        const isAdminEndpoint = /\/api\/v1\/admin(\/|$)/.test(url)
        if (impersonateUserId && !isAdminEndpoint) {
            headers.set(IMPERSONATION_HEADER, impersonateUserId)
            const impersonateAccountId = sessionStorage.getItem(IMPERSONATION_ACCOUNT_ID_KEY)
            if (impersonateAccountId) {
                headers.set('X-Account-Id', impersonateAccountId)
            }
        }
    }

    const response = await fetch(resolveUrl(url), {
        ...options,
        headers,
        credentials: 'include', // Always include cookies for authentication
    })

    if (!response.ok) {
        // Try to extract structured ProblemDetails and VK details for user-friendly message
        let rawText: string | null = null
        let problem: any = null
        try {
            rawText = await response.text()
            try {
                problem = rawText ? JSON.parse(rawText) : null
            } catch {
                problem = null
            }
        } catch {
            rawText = null
            problem = null
        }

        const pickMessage = (): string => {
            // Default fallback
            const message = `HTTP ${response.status}: ${response.statusText}`
            if (!problem || typeof problem !== 'object') return message

            const title: string | undefined = typeof problem.title === 'string' ? problem.title : undefined
            const errorCode: string | undefined = typeof problem.error === 'string' ? problem.error : undefined
            const detailRaw = problem.detail

            // Helper: try parse nested JSON from detail (VK body often placed as string)
            const parseNested = (x: any): any | null => {
                if (!x) return null
                if (typeof x === 'object') return x
                if (typeof x === 'string') {
                    const s = x.trim()
                    if (s.startsWith('{') || s.startsWith('[')) {
                        try { return JSON.parse(s) } catch { return null }
                    }
                    // Try to extract JSON substring from mixed text ("Body: {...}")
                    const start = s.indexOf('{')
                    const end = s.lastIndexOf('}')
                    if (start >= 0 && end > start) {
                        try { return JSON.parse(s.slice(start, end + 1)) } catch { /* ignore */ }
                    }
                    return { error: s }
                }
                return null
            }

            // OrdExceptionFilter error codes — user-friendly messages
            if (errorCode === 'ord_provider_not_configured') {
                return 'Не выбран ОРД-провайдер. Перейдите в Настройки и подключите ОРД.'
            }
            if (errorCode === 'ord_token_invalid') {
                return 'Ключ доступа к ОРД недействителен или истёк. Перейдите в раздел Настройка для переподключения.'
            }
            // 403 от ОРД — это отказ по правам или биллингу, а не проблема с токеном.
            // Показываем формулировку провайдера: она единственная знает настоящую причину.
            if (errorCode === 'ord_forbidden') {
                if (typeof detailRaw === 'string' && detailRaw.trim().length > 0) return detailRaw
                return 'ОРД отклонил операцию. Проверьте права доступа и состояние тарифа в личном кабинете ОРД.'
            }
            if (errorCode === 'ord_unavailable') {
                return 'ОРД-провайдер временно недоступен. Попробуйте позже.'
            }
            if (errorCode === 'ord_unknown_provider') {
                return 'Неизвестный ОРД-провайдер. Проверьте настройки.'
            }

            // VK ORD bad request — parse nested VK error body for friendly messages
            // Matches both old ProblemDetails format (title) and new filter format (error)
            if (title === 'vk_ord_bad_request' || errorCode === 'ord_bad_request') {
                const vk = parseNested(detailRaw)
                // Known VK schema: { error: string, errors: [{ field, message, error_code, values? }] }
                if (vk && typeof vk === 'object') {
                    const vkError: string | undefined = typeof vk.error === 'string' ? vk.error : undefined
                    const first = Array.isArray(vk.errors) && vk.errors.length > 0 ? vk.errors[0] : null
                    const field: string | undefined = first && typeof first.field === 'string' ? first.field : undefined
                    const code: string | undefined = first && typeof first.error_code === 'string' ? first.error_code : undefined
                    const msg: string | undefined = first && typeof first.message === 'string' ? first.message : undefined

                    // Friendly mapping for common cases
                    if (field === 'items.creative_external_id' && (code === 'common_not_found_error' || msg?.toLowerCase().includes('not found'))) {
                        return 'Креатив не найден в ВК ОРД. Выберите публикацию из списка (мы подставим ERID автоматически) или укажите корректный ERID/ID.'
                    }
                    if (field === 'items.pad_external_id' && (code === 'common_not_found_error' || msg?.toLowerCase().includes('not found'))) {
                        return 'Площадка не найдена в ВК ОРД. Выберите площадку из списка подключённых.'
                    }
                    if (msg && msg.toLowerCase().includes('dates should belong to the same month')) {
                        return 'Даты должны быть в одном месяце. Измените период.'
                    }
                    if (field === 'items.date_start_actual' || field === 'items.date_end_actual') {
                        return 'Неверный период показа. Проверьте даты начала и конца.'
                    }
                    // Contract-related errors
                    if (vkError?.includes('contractor_person_not_found') || msg?.toLowerCase().includes('contractor person not found')) {
                        return 'Контрагент-исполнитель не найден в ОРД. Убедитесь, что контрагент зарегистрирован у выбранного провайдера.'
                    }
                    if (vkError?.includes('client_person_not_found') || msg?.toLowerCase().includes('client person not found')) {
                        return 'Контрагент-заказчик не найден в ОРД. Убедитесь, что контрагент зарегистрирован у выбранного провайдера.'
                    }
                    if (vkError?.includes('person_not_found') || code === 'common_not_found_error' && msg?.toLowerCase().includes('person not found')) {
                        return 'Контрагент не найден в ОРД. Убедитесь, что контрагент зарегистрирован у выбранного провайдера.'
                    }
                    if (vkError?.includes('contract_not_found') || msg?.toLowerCase().includes('contract not found')) {
                        return 'Договор не найден в ОРД. Возможно, он был удалён или не зарегистрирован.'
                    }
                    if (code === 'pad_owner_is_incompatible_with_creative_person_id' || msg?.includes('pad owner does not match the person in the self-promotion')) {
                        return 'Владелец площадки не совпадает с контрагентом креатива. Для саморекламы контрагент креатива должен совпадать с владельцем площадки.'
                    }
                    if (vkError) return vkError
                    if (msg) return msg
                }
            }

            // ORD provider error — show detail from provider response
            if (errorCode === 'ord_provider_error' || errorCode === 'ord_not_found') {
                // Ozon отдаёт машинный код отказа — по нему подсказываем, что именно делать.
                // Без этого пользователь видел только формулировку ОРД без пути к решению.
                const providerCode: string | undefined =
                    typeof problem.providerCode === 'string' ? problem.providerCode : undefined

                if (providerCode?.startsWith('0436')) {
                    return 'У контрагента-исполнителя не заполнен адрес регистрации. '
                        + 'Ozon требует его для актов, где исполнитель — ИП или физлицо РФ. '
                        + 'Откройте карточку контрагента в разделе «Контрагенты», заполните адрес и повторите создание акта.'
                }

                if (typeof detailRaw === 'string' && detailRaw.trim().length > 0) return detailRaw
                return 'Ошибка ОРД-провайдера. Попробуйте позже.'
            }

            // Машинный код из Core/ErrorCodes без detail — иначе пользователь видел «HTTP 400: Bad Request»
            const translated = errorCode ? translateErrorCode(errorCode) : undefined
            if (translated) return translated

            // Generic ProblemDetails
            if (typeof detailRaw === 'string' && detailRaw.trim().length > 0) return detailRaw
            if (title) return title
            return message
        }

        // Очищаем имперсонацию только если она реально невалидна (юзер удалён → 404 от
        // ImpersonationMiddleware). 403 «Impersonation is read-only» — ожидаемая блокировка
        // мутаций, она НЕ должна выкидывать админа: он продолжает смотреть кабинет, а UI
        // получает обычную ошибку.
        if (
            response.status === 404 &&
            typeof window !== 'undefined' &&
            sessionStorage.getItem(IMPERSONATION_USER_ID_KEY) &&
            typeof problem?.error === 'string' &&
            problem.error.toLowerCase().includes('impersonated user not found')
        ) {
            clearImpersonation()
            window.location.href = '/admin/users'
            return new Promise<T>(() => {}) // never resolves — page is navigating away
        }

        const err: any = new Error(pickMessage())
        err.status = response.status
        if (problem) err.problem = problem
        if (rawText && !problem) err.body = rawText
        throw err
    }

    // Handle 204 No Content - no body to parse
    if (response.status === 204) {
        return { data: undefined, status: response.status } as T
    }

    const data = await response.json()

    // Return in format expected by generated client
    return { data, status: response.status } as T
}

